import { useMemoizedFn } from 'ahooks';
import { useLocalState } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { IReportData, IReportUpdateParams } from './types';

type ReportInstanceMap = Record<string, IReportData & { show?: boolean; }>;

export interface IUseGlobalReportManagerOptions {
  /** 会话 ID，用于生成缓存 key */
  sessionId?: string;
}

export interface IUseGlobalReportManagerReturn {
  /** 当前激活的报告数据（可能为 null） */
  activeReport: (IReportData & { show?: boolean; }) | null;
  /** 当前激活的报告 document_id（可能为 null） */
  activeDocumentId: string | null;
  /** 打开报告，并设置为当前激活报告 */
  openReport: (report: IReportData) => void;
  /** 更新报告数据（可选传入 document_id，默认更新当前激活报告） */
  updateReport: (update: Partial<IReportUpdateParams> & { document_id?: string; show?: boolean; }) => void;
  /** 关闭报告（默认关闭当前激活报告，仅将 show 置为 false，不删除数据） */
  closeReport: (documentId?: string) => void;
}

export const useGlobalReportManager = (
  options?: IUseGlobalReportManagerOptions
): IUseGlobalReportManagerReturn => {
  const { sessionId = '' } = options || {};
  
  // 缓存 key 以 sessionId 为单位
  const REPORT_INSTANCES_KEY = sessionId 
    ? `__dba_report_instances_${sessionId}__` 
    : '__dba_report_instances__';
  const ACTIVE_REPORT_ID_KEY = sessionId 
    ? `__dba_report_active_document_id_${sessionId}__` 
    : '__dba_report_active_document_id__';

  const [instances, setInstances] = useLocalState<ReportInstanceMap>(REPORT_INSTANCES_KEY, {});
  const [activeDocumentId, setActiveDocumentId] = useLocalState<string | null>(
    ACTIVE_REPORT_ID_KEY,
    null,
  );

  const activeReport = activeDocumentId ? instances[activeDocumentId] || null : null;

  const openReport = useMemoizedFn((report: IReportData) => {
    const documentId = report?.document_id || report?.id;
    if (!documentId) return;

    setInstances((prev) => {
      const next: ReportInstanceMap = {
        ...(prev || {}),
        [documentId]: {
          ...(prev?.[documentId] || ({})),
          ...report,
          // 强制补 document_id，防止 aiExcel/magicPoster 场景下
          // report 只有 id 没有 document_id，导致后续 isReportVisible 判断失败
          document_id: documentId,
          show: true,
        },
      };
      return next;
    });
    setActiveDocumentId(documentId);
  });

  const updateReport = useMemoizedFn(
    (update: Partial<IReportUpdateParams> & { document_id?: string; show?: boolean; }) => {
      const targetId = update.document_id || update.id || activeDocumentId;
      if (!targetId) return;

      setInstances((prev) => {
        const current = prev?.[targetId] || ({});
        const next: ReportInstanceMap = {
          ...(prev || {}),
          [targetId]: {
            ...current,
            ...update,
          },
        };
        return next;
      });
    },
  );

  const closeReport = useMemoizedFn((documentId?: string) => {
    const targetId = documentId || activeDocumentId;
    if (!targetId) return;

    setInstances((prev) => {
      const current = prev?.[targetId];
      if (!current) return prev;
      return {
        ...(prev || {}),
        [targetId]: {
          ...current,
          show: false,
        },
      };
    });

    // 注意：不再清除 activeDocumentId，保持报告数据和 document_id 可访问
    // 这样即使 show=false，报告窗口仍能使用原有参数调用后端接口
  });

  return {
    activeReport,
    activeDocumentId,
    openReport,
    updateReport,
    closeReport,
  };
};

export default useGlobalReportManager;

